import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Check, ChevronDown, Trash2, Pencil, Save, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const vendorSchema = z.object({
  vendor_name: z.string().min(1, "Vendor Name is required"),
  vendor_type: z.string().min(1, "Vendor Type is required"),
  phone_number: z.string().optional(),
  gst_tin: z.string().optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  address: z.string().optional(),
  opening_balance: z.coerce.number().min(0, "Opening Balance must be positive"),
  opening_date: z.string().optional(),
  opening_type: z.string().optional(),
  description: z.string().optional(),
});

type VendorFormData = z.infer<typeof vendorSchema>;

const defaultVendorValues: VendorFormData = {
  vendor_name: "",
  vendor_type: "",
  phone_number: "",
  gst_tin: "",
  email: "",
  address: "",
  opening_balance: 0,
  opening_date: new Date().toISOString().split('T')[0],
  opening_type: "",
  description: "",
};

const BASE_VENDOR_TYPE_OPTIONS = ["Liquids", "Lubricants"];

const parseVendorTypes = (vendorType?: string, vendorTypes?: string[]) => {
  if (Array.isArray(vendorTypes) && vendorTypes.length > 0) return vendorTypes;
  if (vendorType === "Both") return BASE_VENDOR_TYPE_OPTIONS;
  if (vendorType && vendorType.includes(",")) {
    return vendorType.split(",").map((type) => type.trim()).filter(Boolean);
  }
  if (vendorType) return [vendorType];
  return [];
};

const serializeVendorTypes = (selectedTypes: string[]) => {
  const uniqueTypes = Array.from(new Set(selectedTypes.filter(Boolean)));
  return uniqueTypes.join(",");
};

const formatVendorTypes = (vendor: any) => {
  return parseVendorTypes(vendor.vendor_type, vendor.vendor_types).join(",");
};

export default function Vendors() {
  const { toast } = useToast();
  const [vendors, setVendors] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVendorTypes, setSelectedVendorTypes] = useState<string[]>([]);
  const [vendorTypeOpen, setVendorTypeOpen] = useState(false);

  const form = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: defaultVendorValues
  });

  const vendorTypeOptions = useMemo(() => {
    const fetchedTypes = vendors.flatMap((vendor) => parseVendorTypes(vendor.vendor_type, vendor.vendor_types));
    return Array.from(new Set([...BASE_VENDOR_TYPE_OPTIONS, ...fetchedTypes].filter(Boolean)));
  }, [vendors]);

  const setVendorTypes = (types: string[]) => {
    const nextTypes = Array.from(new Set(types.filter(Boolean)));
    setSelectedVendorTypes(nextTypes);

    form.setValue("vendor_type", serializeVendorTypes(nextTypes), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const addVendorType = (type: string) => {
    if (selectedVendorTypes.includes(type)) return;
    setVendorTypes([...selectedVendorTypes, type]);
  };

  const removeVendorType = (type: string) => {
    setVendorTypes(selectedVendorTypes.filter((selectedType) => selectedType !== type));
  };

  const fetchVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/vendors', {
        credentials: 'include'
      });
      const result = await response.json();

      if (result.ok) {
        setVendors(result.rows || []);
      } else {
        toast({ variant: "destructive", title: "Error", description: result.error || "Failed to fetch vendors" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to fetch vendors" });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const onSubmit = async (data: VendorFormData) => {
    const vendorType = serializeVendorTypes(selectedVendorTypes);
    const submitData = {
      ...data,
      vendor_type: vendorType,
      vendor_types: selectedVendorTypes,
      phone_number: data.phone_number || null,
      gst_tin: data.gst_tin || null,
      email: data.email || null,
      address: data.address || null,
      opening_date: data.opening_date || null,
      opening_type: data.opening_type || null,
      description: data.description || null,
      current_balance: data.opening_balance, // Initialize current balance with opening balance
    };

    try {
      if (editingId) {
        const response = await fetch(`/api/vendors/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(submitData)
        });
        const result = await response.json();

        if (result.ok) {
          toast({ title: "Success", description: "Vendor updated successfully" });
          setEditingId(null);
          form.reset(defaultVendorValues);
          setSelectedVendorTypes([]);
          fetchVendors();
        } else {
          toast({ variant: "destructive", title: "Error", description: result.error || "Failed to update vendor" });
        }
      } else {
        const response = await fetch('/api/vendors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(submitData)
        });
        const result = await response.json();

        if (result.ok) {
          toast({ title: "Success", description: "Vendor added successfully" });
          form.reset(defaultVendorValues);
          setSelectedVendorTypes([]);
          fetchVendors();
        } else {
          toast({ variant: "destructive", title: "Error", description: result.error || "Failed to add vendor" });
        }
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to save vendor" });
    }
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    const itemVendorTypes = parseVendorTypes(item.vendor_type, item.vendor_types);
    setSelectedVendorTypes(itemVendorTypes);
    form.reset({
      vendor_name: item.vendor_name,
      vendor_type: serializeVendorTypes(itemVendorTypes),
      phone_number: item.phone_number || "",
      gst_tin: item.gst_tin || "",
      email: item.email || "",
      address: item.address || "",
      opening_balance: parseFloat(item.opening_balance) || 0,
      opening_date: item.opening_date ? new Date(item.opening_date).toISOString().split('T')[0] : "",
      opening_type: item.opening_type || "",
      description: item.description || "",
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`/api/vendors/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const result = await response.json();

      if (result.ok) {
        toast({ title: "Success", description: "Vendor deleted" });
        fetchVendors();
      } else {
        toast({ variant: "destructive", title: "Error", description: result.error || "Failed to delete vendor" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to delete vendor" });
    }
    setDeleteConfirm(null);
  };

  const handleToggleStatus = async (vendorId: string, currentStatus: boolean) => {
    try {
      const response = await fetch(`/api/vendors/${vendorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !currentStatus })
      });
      const result = await response.json();

      if (result.ok) {
        toast({ title: "Success", description: `Vendor ${!currentStatus ? 'activated' : 'deactivated'} successfully` });
        fetchVendors();
      } else {
        toast({ variant: "destructive", title: "Error", description: result.error || "Failed to update status" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to update status" });
    }
  };

  const filteredVendors = vendors.filter((item) =>
    (item.vendor_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.vendor_type || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredVendors.length;
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalItems / itemsPerPage);
  const startIndex = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === 'all' ? totalItems : startIndex + itemsPerPage;
  const paginatedVendors = itemsPerPage === 'all' ? filteredVendors : filteredVendors.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      <Card className="border-t-4 border-t-blue-600 shadow-md">
        <CardHeader className="bg-blue-600 text-white py-3">
          <CardTitle className="text-lg font-medium">Create Vendor</CardTitle>
        </CardHeader>
        <CardContent className="p-6 bg-blue-50">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-1">
                <Label htmlFor="vendor_name" className="text-blue-900 font-semibold">Vendor Name <span className="text-red-500">*</span></Label>
                <Input
                  id="vendor_name"
                  {...form.register("vendor_name")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="Vendor Name"
                />
                {form.formState.errors.vendor_name && (
                  <p className="text-xs text-red-500 mt-1">{form.formState.errors.vendor_name.message}</p>
                )}
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="vendor_type" className="text-blue-900 font-semibold">Vendor Type <span className="text-red-500">*</span></Label>
                <Popover open={vendorTypeOpen} onOpenChange={setVendorTypeOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="vendor_type"
                      type="button"
                      className="flex min-h-10 w-full items-center justify-between rounded-md border border-blue-200 bg-white px-3 py-2 text-left text-sm text-black focus:border-blue-500"
                    >
                      <span className="truncate">
                        {selectedVendorTypes.length > 0 ? selectedVendorTypes.join(", ") : "Choose type"}
                      </span>
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-64 p-1">
                    {vendorTypeOptions.map((type) => {
                      const isSelected = selectedVendorTypes.includes(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => addVendorType(type)}
                          className="flex w-full items-center justify-between rounded px-3 py-2 text-sm hover:bg-blue-50"
                        >
                          <span>{type}</span>
                          {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                        </button>
                      );
                    })}
                  </PopoverContent>
                </Popover>
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedVendorTypes.map((type) => (
                    <Badge key={type} variant="secondary" className="gap-1 rounded-md bg-blue-100 text-blue-800">
                      {type}
                      <button type="button" onClick={() => removeVendorType(type)} aria-label={`Remove ${type}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                {form.formState.errors.vendor_type && (
                  <p className="text-xs text-red-500 mt-1">{form.formState.errors.vendor_type.message}</p>
                )}
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="phone_number" className="text-blue-900 font-semibold">Phone Num</Label>
                <Input
                  id="phone_number"
                  {...form.register("phone_number")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="Phone Num"
                />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="gst_tin" className="text-blue-900 font-semibold">TIN / GST NO</Label>
                <Input
                  id="gst_tin"
                  {...form.register("gst_tin")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="TIN / GST NO"
                />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="email" className="text-blue-900 font-semibold">Email ID</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register("email")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="Email ID"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <Label htmlFor="address" className="text-blue-900 font-semibold">Address</Label>
                <Input
                  id="address"
                  {...form.register("address")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="Address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="opening_balance" className="text-blue-900 font-semibold">Opening Balance</Label>
                <Input
                  id="opening_balance"
                  type="number"
                  step="0.01"
                  {...form.register("opening_balance", { valueAsNumber: true })}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="opening_date" className="text-blue-900 font-semibold">Opening Date</Label>
                <Input
                  id="opening_date"
                  type="date"
                  {...form.register("opening_date")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                />
                <div className="text-[10px] text-gray-500 mt-1">Balance Cutoff Date (Ex:31-Mar-202X)</div>
              </div>
              <div>
                <Label htmlFor="opening_type" className="text-blue-900 font-semibold">Opening Type</Label>
                <Select
                  onValueChange={(value) => form.setValue("opening_type", value)}
                  defaultValue={form.getValues("opening_type")}
                  value={form.watch("opening_type")}
                >
                  <SelectTrigger className="bg-white border-blue-200 focus:border-blue-500">
                    <SelectValue placeholder="Choose Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Payable">Payable</SelectItem>
                    <SelectItem value="Receivable">Receivable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description" className="text-blue-900 font-semibold">Description</Label>
                <Input
                  id="description"
                  {...form.register("description")}
                  className="bg-white border-blue-200 focus:border-blue-500"
                  placeholder="Description"
                />
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button type="submit" className="bg-[#84cc16] hover:bg-[#65a30d] text-white px-8 font-bold rounded-full">
                <Save className="h-4 w-4 mr-2" /> {editingId ? "UPDATE" : "SAVE"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="p-4 flex justify-between items-center bg-white border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <select 
                className="border rounded p-1 text-sm bg-white"
                value={itemsPerPage}
                onChange={(e) => {
                  const value = e.target.value === 'all' ? 'all' : Number(e.target.value);
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="10">10</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Filter:</span>
                <Input
                  placeholder="Type to filter..."
                  className="h-8 w-48"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="font-bold text-gray-700">S.No</TableHead>
                  <TableHead className="font-bold text-gray-700">Vendor Name</TableHead>
                  <TableHead className="font-bold text-gray-700">Phone Num</TableHead>
                  <TableHead className="font-bold text-gray-700">GSTIN</TableHead>
                  <TableHead className="font-bold text-gray-700">Type</TableHead>
                  <TableHead className="font-bold text-gray-700">Opening Balance</TableHead>
                  <TableHead className="font-bold text-gray-700">Email</TableHead>
                  <TableHead className="font-bold text-gray-700">Address</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Action</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Status</TableHead>
                  <TableHead className="font-bold text-gray-700">User Log Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedVendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      No vendors found. Add your first vendor above.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVendors.map((item, index) => (
                    <TableRow key={item.id} className="hover:bg-gray-50">
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell className="font-medium">{item.vendor_name}</TableCell>
                      <TableCell>{item.phone_number || "N/A"}</TableCell>
                      <TableCell>{item.gst_tin || "N/A"}</TableCell>
                      <TableCell>{formatVendorTypes(item)}</TableCell>
                      <TableCell>₹{item.opening_balance || 0}</TableCell>
                      <TableCell>{item.email || "N/A"}</TableCell>
                      <TableCell>{item.address || "N/A"}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleEdit(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-orange-400 hover:text-orange-500 hover:bg-orange-50"
                            onClick={() => setDeleteConfirm(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-2">
                          <button
                            onClick={() => handleToggleStatus(item.id, item.is_active ?? true)}
                            className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              (item.is_active ?? true)
                                ? 'bg-blue-600 focus:ring-blue-500' 
                                : 'bg-gray-300 focus:ring-gray-400'
                            }`}
                            role="switch"
                            aria-checked={item.is_active ?? true}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                                (item.is_active ?? true) ? 'translate-x-9' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span 
                            className={`text-xs font-semibold px-2 py-1 rounded ${
                              (item.is_active ?? true)
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {(item.is_active ?? true) ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        Created: Super Admin {item.created_at ? new Date(item.created_at).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="p-4 border-t text-sm text-gray-500 flex justify-between items-center">
            <span>
              Showing {paginatedVendors.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
              {searchTerm && ` (filtered from ${vendors.length} total entries)`}
            </span>
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={currentPage === 1 || itemsPerPage === 'all'}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &larr;
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  className={currentPage === page ? "bg-blue-100" : ""}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
              <Button 
                variant="outline" 
                size="sm" 
                disabled={currentPage === totalPages || itemsPerPage === 'all'}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &rarr;
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will delete this vendor. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteConfirm && handleDelete(deleteConfirm)} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

